import { useState } from 'react';
import styled from 'styled-components';
import { useGetKspRdPositionsQuery } from '@shared/api/endpoints/ksp';
import { apiErrorText } from '@shared/api/errorText';
import type { FieldRdPosition } from '@entities/field';
import { Input } from '@shared/ui/Input';
import { Button } from '@shared/ui/Button';

const PAGE_SIZE = 25;
const Options = styled.div`
  display: grid;
  gap: 6px;
  max-height: 340px;
  overflow: auto;
  margin: 8px 0;
`;
const Option = styled.button<{ $selected: boolean }>`
  width: 100%;
  text-align: left;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.border)};
  color: inherit;
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;
const Detail = styled.small`
  display: block;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 4px;
`;
const Pager = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

/** КСП хранит точный position_id: одноимённые строки разных листов не склеиваем.
 *  Номер строки ведомости объёмов ПТО («СП01 №5») — сразу после шифра. */
function positionLabel(position: FieldRdPosition & { document_code?: string }) {
  const mark = position.designation || position.code;
  return `${position.document_code ? `${position.document_code} · ` : ''}${position.row_label ? `${position.row_label} · ` : ''}${mark ? `${mark} — ` : ''}${position.name}${position.qualifier ? ` (${position.qualifier})` : ''} · ${position.quantity === null ? 'объём не указан' : position.quantity.toLocaleString('ru-RU')}${position.unit ? ` ${position.unit}` : ''} · ${position.source_ref} · лист ${position.page_number}`;
}

/** Часть РД позиции: подпись части («СП01 · Спецификация …»), у позиции ИИ под ней
 *  прежний `section` (узел или система); подписи нет — прежний `section`. */
function partNotes(position: FieldRdPosition): (string | null)[] {
  const part = position.part_label?.trim();
  if (!part) return [position.section];
  const node = position.source_role === 'rd' ? position.section?.trim() : null;
  return [part, node ? `узел/система: ${node}` : null];
}

/** Серверные страницы сохраняют доступ ко всем каноническим позициям проекта или титула фронта. */
export function RdPositionPicker({
  projectId,
  siteId,
  value,
  onChange,
  id,
  disabled,
}: {
  projectId: string;
  siteId: string;
  value: string;
  onChange: (value: string) => void;
  id: string;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [chosenLabel, setChosenLabel] = useState('');
  const query = useGetKspRdPositionsQuery(
    {
      projectId,
      field_site_id: siteId || undefined,
      q: search || undefined,
      limit: PAGE_SIZE,
      offset,
    },
    { skip: !projectId },
  );
  const positions = query.currentData?.positions ?? [];
  const total = query.currentData?.total ?? 0;
  return (
    <div>
      <Input
        id={id}
        value={search}
        disabled={disabled}
        onChange={(event) => {
          setSearch(event.target.value);
          setOffset(0);
        }}
        placeholder="Найдите позицию РД по названию, шифру или листу"
      />
      {value && (
        <Detail>
          Выбрано:{' '}
          {chosenLabel ||
            (positions.find((p) => p.id === value) &&
              positionLabel(positions.find((p) => p.id === value)!)) ||
            value}
        </Detail>
      )}
      <Options role="listbox" aria-label="Позиции РД для привязки">
        {positions.map((position) => (
          <Option
            key={position.id}
            type="button"
            role="option"
            aria-selected={value === position.id}
            $selected={value === position.id}
            disabled={disabled || query.isFetching}
            onClick={() => {
              setChosenLabel(positionLabel(position));
              onChange(position.id);
            }}
          >
            {positionLabel(position)}
            <Detail>
              {[
                ...partNotes(position),
                position.source_role === 'rd_table'
                  ? 'таблица РД'
                  : position.source_role === 'rd_geometry'
                    ? 'геометрия РД'
                    : 'текст РД',
                position.missing_in_extraction
                  ? 'нет в последнем извлечении — проверьте лист'
                  : position.needs_review && position.decision !== 'accepted'
                    ? 'нужна проверка инженера'
                    : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Detail>
          </Option>
        ))}
      </Options>
      <Pager aria-label="Страницы позиций РД">
        <Button
          $size="sm"
          $variant="ghost"
          disabled={disabled || query.isFetching || offset === 0}
          onClick={() => setOffset((n) => Math.max(0, n - PAGE_SIZE))}
        >
          Предыдущие позиции
        </Button>
        <small>
          {total
            ? `${offset + 1}–${Math.min(offset + positions.length, total)} из ${total}`
            : '0 позиций'}
        </small>
        <Button
          $size="sm"
          $variant="ghost"
          disabled={disabled || query.isFetching || offset + positions.length >= total}
          onClick={() => setOffset((n) => n + PAGE_SIZE)}
        >
          Следующие позиции
        </Button>
        {value && (
          <Button
            $size="sm"
            $variant="ghost"
            disabled={disabled}
            onClick={() => {
              onChange('');
              setChosenLabel('');
            }}
          >
            Очистить выбор
          </Button>
        )}
      </Pager>
      {query.isFetching && <Detail>Загрузка позиций…</Detail>}
      {!siteId && (
        <Detail>
          Поиск по всему проекту. Для факта Прораба по титулу выберите точную площадку / фронт.
        </Detail>
      )}
      {query.error && (
        <small role="alert">
          {apiErrorText(query.error, 'Не удалось загрузить РД проекта КСП.')}
        </small>
      )}
      {!query.isFetching && !query.error && positions.length === 0 && (
        <Detail>
          Подходящих позиций нет. Уточните поиск или проверьте РД и принятые объёмы у инженера.
        </Detail>
      )}
    </div>
  );
}
